import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** What a paginated feed needs to come back exactly as the user left it. */
export interface FeedSnapshot<T = any> {
  /** URL the feed was on when the snapshot was taken. */
  url: string;
  items: T[];
  currentPage: number;
  total: number;
  allLoaded: boolean;
  scrollY: number;
}

/**
 * Keeps a feed's loaded pages + scroll position alive while the user opens a
 * post's detail screen, so coming back (browser back or the in-page back
 * button) lands them on the same card instead of a fresh page 1 at the top.
 *
 * Snapshots are dropped as soon as the user navigates anywhere other than a
 * post detail or back to the feed itself, so a later visit starts fresh.
 */
@Injectable({ providedIn: 'root' })
export class FeedSnapshotService {
  private router = inject(Router);
  private snapshots = new Map<string, FeedSnapshot>();

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        const url = e.urlAfterRedirects;
        if (url.startsWith('/post/')) return;
        for (const [key, snap] of this.snapshots) {
          if (snap.url !== url) this.snapshots.delete(key);
        }
      });
  }

  save<T>(key: string, snapshot: FeedSnapshot<T>): void {
    this.snapshots.set(key, snapshot);
  }

  /** Returns the snapshot for `key` (if it belongs to `url`) and forgets it. */
  take<T>(key: string, url: string): FeedSnapshot<T> | undefined {
    const snap = this.snapshots.get(key);
    this.snapshots.delete(key);
    return snap && snap.url === url ? (snap as FeedSnapshot<T>) : undefined;
  }
}
